import { ReactNode, useEffect, useState } from "react";
import { downloadCsv, type CsvColumn } from "../../utils/csv";
import Pagination from "src/components/Pagination/Pagination";

export interface DataColumn<T> {
	key: string;
	header: string;
	align?: "left" | "right";
	render: (row: T) => ReactNode;
	csv?: (row: T) => string | number | null | undefined;
}

export interface DataTablePagination {
	/** Items per page. */
	pageSize: number;
	/** Word used in the summary, e.g. "entities", "hashtags". Defaults to "items". */
	itemLabel?: string;
}

interface Props<T> {
	rows: readonly T[];
	columns: readonly DataColumn<T>[];
	caption?: string;
	csvFilename?: string;
	emptyText?: string;
	onRowClick?: (row: T) => void;
	loading?: boolean;
	rowKey: (row: T) => string;
	pagination?: DataTablePagination;
}

function DataTable<T>({
	rows,
	columns,
	caption,
	csvFilename,
	emptyText = "No rows.",
	onRowClick,
	loading,
	rowKey,
	pagination
}: Props<T>) {
	const [page, setPage] = useState(1);

	useEffect(() => {
		setPage(1);
	}, [rows, pagination?.pageSize]);

	const pageSize = pagination?.pageSize;
	const totalCount = rows.length;
	// Clamp rather than blindly slicing — when rows shrink (e.g. a
	// poll returns fewer items) we want the user to land on the last
	// available page, not on an empty one beyond the end.
	const totalPages = pageSize
		? Math.max(1, Math.ceil(totalCount / pageSize))
		: 1;
	const safePage = Math.min(page, totalPages);
	const visibleRows = pageSize
		? rows.slice((safePage - 1) * pageSize, safePage * pageSize)
		: rows;
	const csvColumns: CsvColumn<T>[] = columns
		.filter((c) => c.csv !== undefined)
		.map((c) => ({
			header: c.header,
			accessor: (row: T) => c.csv!(row)
		}));

	const exportCsv = () => {
		if (!csvFilename) return;
		downloadCsv(csvFilename, rows, csvColumns);
	};

	return (
		<div className="rounded-xl border border-border bg-white shadow-sm overflow-hidden">
			{caption || csvFilename ? (
				<div className="flex items-center justify-between px-4 py-2 border-b border-border bg-subtle">
					{caption ? (
						<p className="text-sm font-medium text-foreground">{caption}</p>
					) : (
						<span />
					)}
					{csvFilename && rows.length > 0 ? (
						<button
							type="button"
							onClick={exportCsv}
							className="text-xs font-medium text-primary hover:underline"
						>
							Export CSV
						</button>
					) : null}
				</div>
			) : null}
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-border bg-white">
							{columns.map((c) => (
								<th
									key={c.key}
									className={`py-2 px-4 font-medium text-muted-foreground ${
										c.align === "right" ? "text-right" : "text-left"
									}`}
								>
									{c.header}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td
									colSpan={columns.length}
									className="py-8 text-center text-muted-foreground text-sm"
								>
									<span className="inline-block h-3 w-24 rounded animate-pulse bg-zinc-200" />
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td
									colSpan={columns.length}
									className="py-8 text-center text-muted-foreground text-sm"
								>
									{emptyText}
								</td>
							</tr>
						) : (
							visibleRows.map((row) => (
								<tr
									key={rowKey(row)}
									className={`border-b border-border last:border-0 ${
										onRowClick
											? "cursor-pointer hover:bg-subtle focus-within:bg-subtle"
											: ""
									}`}
									// When the row is interactive, expose it to keyboard
									// without overriding the row role — `role="button"`
									// on `<tr>` is invalid ARIA (a button cannot be a
									// child of a table) and removes the row from the
									// table accessibility tree, breaking row/column
									// navigation for screen-reader users. We keep the
									// native `row` role and just add `tabIndex` + a key
									// handler. The visible cursor / hover / focus styles
									// communicate interactivity to sighted users.
									tabIndex={onRowClick ? 0 : undefined}
									onClick={onRowClick ? () => onRowClick(row) : undefined}
									onKeyDown={
										onRowClick
											? (e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														onRowClick(row);
													}
												}
											: undefined
									}
								>
									{columns.map((c) => (
										<td
											key={c.key}
											className={`py-2.5 px-4 text-foreground ${
												c.align === "right" ? "text-right" : "text-left"
											}`}
										>
											{c.render(row)}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{pageSize && !loading && totalCount > pageSize ? (
				<div className="px-4 pb-3">
					<Pagination
						page={safePage}
						pageSize={pageSize}
						totalCount={totalCount}
						onPageChange={setPage}
						itemLabel={pagination?.itemLabel ?? "items"}
					/>
				</div>
			) : null}
		</div>
	);
}

export default DataTable;
